import type { Inbox, InboxEntry, KnowledgeInboxEntry } from "../../../api/content/reviewCollaborationApi";
import { proposalPath } from "../../knowledgeData";
import { reviewAddress } from "../../reviews";

/** A waiting item no shown Run row covers: a row of its own in the sidebar, as a Run's row (`WaitingItemRow`). */
export interface RunlessItem {
  /** What it is, named where a Run's name would sit (Johannes, 5 Nov 08:44: no Run's look for what isn't a Run). */
  readonly kind: "changeSet" | "knowledgeData";
  readonly key: string;
  /** A change set's title; `null` for a knowledge data result, named by its document. */
  readonly title: string | null;
  readonly document: string | null;
  readonly href: string;
  readonly at: string;
}

/**
 * What waits for the person and no Run row in view owns (IM-THREADS: reviews waiting for you show in the sidebar's Input
 * group; Johannes, 4 Nov 23:04: "one run is one thread like in t3 code, we don't dublicate jobs"; the orchestrator, 5 Nov
 * 08:32: an item whose Run is listed is that Run's row). `inView`: the Runs whose rows are pinned or active. A review
 * belongs to its deciding Run and the Runs it came from, sent back too; a result to the Run that made it. A Run folded
 * under Working, Snoozed or Settled leaves its item a row of its own, the one entrance (4 Nov 23:27).
 */
export function runlessWaiting(inbox: Inbox, inView: ReadonlySet<string>): readonly RunlessItem[] {
  const owned = (runs: readonly string[]) => runs.some((run) => inView.has(run));
  return [
    ...reviewItems([...inbox.waitingForMe, ...inbox.sentBackToMe].filter((entry) => !owned(madeBy(entry)))),
    ...resultItems(inbox.knowledgeData.waitingForMe.filter((entry) => !owned([runOf(entry.source)]))),
  ];
}

/** The person's own open proposals no listed Run made: a folded shelf, so nothing is lost (23:17, 23:27). */
export function runlessProposals(inbox: Inbox, listed: ReadonlySet<string>): readonly RunlessItem[] {
  const unlisted = (runs: readonly string[]) => !runs.some((run) => listed.has(run));
  return [
    ...reviewItems(inbox.myOpenProposals.filter((entry) => unlisted(madeBy(entry)))),
    ...resultItems(inbox.knowledgeData.myProposals.filter((entry) => unlisted([runOf(entry.source)]))),
  ];
}

/** The Runs a review or result waiting for the person belongs to: never folded away under Shared (`threadSections`). */
export function runsWaitingForMe(inbox: Inbox): ReadonlySet<string> {
  const reviews = [...inbox.waitingForMe, ...inbox.sentBackToMe].flatMap(madeBy);
  return new Set([...reviews, ...inbox.knowledgeData.waitingForMe.map((entry) => runOf(entry.source))]);
}

function reviewItems(entries: readonly InboxEntry[]): RunlessItem[] {
  return entries.map(({ review }) => ({ kind: "changeSet" as const, key: review.changeSet.changeSetId, title: review.changeSet.title, document: null, href: reviewAddress(review), at: review.lastDecisionAt ?? review.changeSet.openedAt }));
}

function resultItems(entries: readonly KnowledgeInboxEntry[]): RunlessItem[] {
  return entries.map((entry) => ({ kind: "knowledgeData" as const, key: entry.source, title: null, document: entry.document, href: proposalPath(entry.source), at: entry.recordedAt }));
}

/** The Runs a proposal came from: the Run whose step decides it, and those it was made in. */
function madeBy({ review }: InboxEntry): readonly string[] {
  return [...(review.reviewSubject === null ? [] : [review.reviewSubject.threadId]), ...review.changeSet.originThreadEntries.map(runOf)];
}

/** The Run of a Thread entry reference, `<thread>#<entry>`. */
function runOf(entry: string): string {
  return entry.split("#")[0] ?? entry;
}
